import React, { useEffect, useRef, useState, useMemo, Suspense, lazy } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { getRotationSpeed, getAxialTilt, calculateRingVelocities, getEarthRotationAngle } from '../../utils/PlanetUtils';
import { PLANET_DATA } from '../../utils/PlanetConstants';
import PaleoEarthTimeline from './PaleoEarthTimeline';
import { useRingPrefab } from '../../prefabs/UseRingPrefab';
import * as THREE from 'three';
import SpeechBubble from './SpeechBubble';
import { PlanetLabel } from './SpeechBubble';
import { SatelliteView }  from '../satellite_tracker/SatelliteViewer'
import { SatelliteFilterControls } from '../satellite_tracker/SatelliteFilterControlDashboard';
import { NASAImageGallery } from '../explore/NASAImageGallery';
const LazyExploreISS = lazy(() =>
  import('../explore/ExploreISS').then((mod) => ({ default: mod.ExploreISS }))
);
const LazyExploreHST = lazy(() =>
  import('../explore/ExploreHST').then((mod) => ({ default: mod.ExploreHST }))
);

const LazyMarsExploration = lazy(() =>
  import('../mars_exploration/MarsExploration')
);

function SpinningPlanet({
  planet,
  planetModel,
  texture,
  paleoTexture,
  ringTextures = {},
  rotationSpeed = 0.01,
  materialCacheRef,
  selectedMeshInfo,
  setSelectedMeshInfo,
  isTrackingSatellite = false,
  onSpinComplete,
  resetSpinTrigger,
}) {
  const tiltGroupRef = useRef();
  const meshRef = useRef();
  const texturesAppliedRef = useRef({});

  const radius = 1.5;
  const axialTilt = getAxialTilt(planet.name);
  const ringObject = useRingPrefab(planet.name);

  const [fadeScaleResetTrigger, setFadeScaleResetTrigger] = useState(0);
  const opacityRef = useRef(0);
  const scaleRef = useRef(0);
  const opacityResetPendingRef = useRef(false);

  const shouldCompleteSpinRef = useRef(false);
  const targetRotationRef = useRef(0);
  const currentRotationSpeedRef = useRef(rotationSpeed);
  const prevTrackingRef = useRef(isTrackingSatellite);


  // Memoize planetTextures object so it's stable across renders
  const planetTextures = useMemo(() => ({
    Saturn: ringTextures?.saturn,
    Jupiter: ringTextures?.jupiter,
    Uranus: ringTextures?.uranus,
    Neptune: ringTextures?.neptune,
  }), [ringTextures]);

  // Memoize sectionVelocities to avoid recalculating unnecessarily
  const sectionVelocities = useMemo(() => calculateRingVelocities(), []);

  const RING_ROTATION_VISUAL_SCALE = 7500;

  // Memoize ringRotationSpeeds to avoid recalculating unless sectionVelocities change
  const ringRotationSpeeds = useMemo(() => ({
    inner: sectionVelocities.inner * RING_ROTATION_VISUAL_SCALE,
    main: sectionVelocities.main * RING_ROTATION_VISUAL_SCALE,
    outer: sectionVelocities.outer * RING_ROTATION_VISUAL_SCALE,
  }), [sectionVelocities]);


  const savedRotationRef = useRef({ x: 0, y: 0, z: 0 });

  // Handle click inside component to update selected mesh
  function handleClick(event, meshName) {
    event.stopPropagation();
    // console.log(`Clicked on ${meshName}`);
    if (!meshName || meshName === "Planet") return;  // Ignore if no valid mesh clicked

    const position = event.intersections?.[0]?.object?.getWorldPosition(new THREE.Vector3());
    setSelectedMeshInfo((prev) =>
      prev?.name === meshName ? null : { name: meshName, position }
    );
  }

  useEffect(() => {
    setFadeScaleResetTrigger((prev) => prev + 1);
  }, [planet.name]);

  useEffect(() => {
    opacityRef.current = 0;
    scaleRef.current = 0;
  }, [fadeScaleResetTrigger]);

  useEffect(() => {
    if (planet.name !== 'Earth' || !planetModel?.scene) return;

    const activeTexture = isTrackingSatellite ? texture : (paleoTexture || texture);
    if (!activeTexture) return;

    activeTexture.flipY = false;
    activeTexture.mapping = THREE.UVMapping;

    // NEW — skip mipmap generation. Building a mipmap pyramid on first
    // upload is a real, synchronous, main-thread-blocking cost — cheap on
    // desktop GPUs, but noticeable enough on mobile to visibly stall the
    // rotation for a frame or two exactly when a new paleo texture commits.
    activeTexture.generateMipmaps = false;
    activeTexture.minFilter = THREE.LinearFilter; // must pair with generateMipmaps=false —
    activeTexture.magFilter = THREE.LinearFilter; // mipmap-based filters (default minFilter) will warn/misbehave otherwise

    activeTexture.needsUpdate = true;

    planetModel.scene.traverse((child) => {
      if (!child.isMesh) return;
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      for (const material of materials) {
        if (!material) continue;
        if (material.map === activeTexture) continue;
        material.map = activeTexture;
        material.needsUpdate = true;
      }
    });
  }, [isTrackingSatellite, paleoTexture, texture, planet.name, planetModel]);

  useEffect(() => {
    if (!planetModel?.scene || !texture) return;
    console.log('Planet Model Children:', planetModel.scene.children);
    texture.flipY = false;
    texture.mapping = THREE.EquirectangularReflectionMapping;
    planetModel.scene.traverse((child) => {
      if (child.isMesh) {
        console.log(`Mesh Found: ${child.name}`, child);
        if (texturesAppliedRef.current[child.name] === texture) return;

        if (!materialCacheRef.current[child.name]) {
          const material = new THREE.MeshStandardMaterial({
            map: texture,
            metalness: 0.1,
            roughness: 0.7,
            // color: new THREE.Color(0xffffff), // ensure no tinting
            transparent: true,
            opacity: 0,  
          });

          materialCacheRef.current[child.name] = material;
          child.material = material;
          texturesAppliedRef.current[child.name] = texture;
        } else {
          child.material = materialCacheRef.current[child.name];
        }

        child.material.transparent = true;
        child.material.opacity = 0;
        child.material.depthTest = true;
        child.material.depthWrite = true;
        child.material.needsUpdate = true;
      }
    });
    if (meshRef.current) {
      const primeMeridianOffset = PLANET_DATA[planet.name].J2000PrimeMeridianOffset;
      const rotationY = getEarthRotationAngle(primeMeridianOffset);
      meshRef.current.rotation.y = rotationY;
    }

  }, [planetModel, texture]);

  useEffect(() => {
    if (tiltGroupRef.current) {
      const correctedTilt = planet.name === 'Uranus' ? Math.PI / 2 : -axialTilt;
      tiltGroupRef.current.rotation.z = correctedTilt;
    }
  }, [axialTilt, planet.name]);

  // Add/remove ringObject to/from scene group
  useEffect(() => {
    const tiltGroup = tiltGroupRef.current;

    if (ringObject && tiltGroup) {
      tiltGroup.add(ringObject);
    }

    return () => {
      if (ringObject && tiltGroup) {
        tiltGroup.remove(ringObject);
      }
    };
  }, [ringObject]);

  // Apply ring textures once ringObject and textures are ready
  useEffect(() => {
    if (!ringObject) return;

    // const planetTextures = {
    //   Saturn: ringTextures?.saturn,
    //   Jupiter: ringTextures?.jupiter,
    //   Uranus: ringTextures?.uranus,
    //   Neptune: ringTextures?.neptune,
    // };
    const planetTexture = planetTextures[planet.name];

    if (
      !planetTexture?.inner ||
      !planetTexture?.main ||
      !planetTexture?.outer
    ) {
      console.warn('Waiting for textures to load:', planetTexture);
      return;
    }

    ['inner', 'main', 'outer'].forEach((key) => {
      const tex = planetTexture[key];
      if (tex) {
        tex.flipY = false;
        tex.wrapS = THREE.RepeatWrapping;
        tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(0, 1);
      }
    });

    const applyTextureToMesh = (group, textureKey) => {
      if (!group) {
        console.warn('No group provided for textureKey:', textureKey);
        return;
      }

      // Find the first child mesh inside the group (if multiple, you might need to adjust)
      const mesh = group.children.find(child => child.isMesh);
      if (!mesh) {
        console.warn('No mesh found inside group:', group);
        return;
      }

      const textureToApply = planetTexture[textureKey];
      if (!textureToApply) {
        console.warn(`No texture found for key: ${textureKey}`);
        return;
      }

      if (texturesAppliedRef.current[mesh.name] === textureToApply) {
        // Texture already applied
        return;
      }

      if (!materialCacheRef.current[mesh.name]) {
        const material = new THREE.MeshStandardMaterial({
          map: textureToApply,
          transparent: true,
          alphaTest: 0.05,
          side: THREE.DoubleSide,
        });

        materialCacheRef.current[mesh.name] = material;

        mesh.material = material;
        mesh.material.transparent = true;
        mesh.material.depthWrite = false;
        mesh.material.needsUpdate = true;

        texturesAppliedRef.current[mesh.name] = textureToApply;
      } else {
        mesh.material = materialCacheRef.current[mesh.name];
      }
    };

    applyTextureToMesh(ringObject.children[0], 'inner');
    applyTextureToMesh(ringObject.children[1], 'main');
    applyTextureToMesh(ringObject.children[2], 'outer');
  }, [ringObject, ringTextures, planet.name]);


  // use if you want the ringobjects materials to be dynamic
  // useEffect(() => {
  //   if (!ringObject) return;
  //   const planetTextures = {
  //     'Saturn': ringTextures?.saturn,
  //     'Jupiter': ringTextures?.jupiter,
  //     'Uranus': ringTextures?.uranus,
  //     'Neptune': ringTextures?.neptune,
  //   };

  //   const planetTexture = planetTextures[planet.name];
  //   if (!planetTexture?.inner || !planetTexture?.main || !planetTexture?.outer) {
  //     console.warn('Waiting for ringObject or textures to load...');
  //     return;
  //   }

  //   // Apply texture properties only once
  //   [planetTexture.inner, planetTexture.main, planetTexture.outer].forEach(tex => {
  //     if (tex) {
  //       tex.wrapS = THREE.RepeatWrapping;
  //       tex.wrapT = THREE.RepeatWrapping;
  //       tex.repeat.set(0, 1);
  //     }
  //   });

  //   // Traverse children once
  //   ringObject.traverse((child) => {
  //     console.log(child.name);
  //     if (!child.isMesh) return;
  //     let textureToApply = null;
  //     if (child.name.includes('Inner')) {
  //       textureToApply = planetTexture.inner;
  //     } else if (child.name.includes('Main')) {
  //       textureToApply = planetTexture.main;
  //     } else if (child.name.includes('Outer')) {
  //       textureToApply = planetTexture.outer;
  //     }

  //     if (!textureToApply) return;
  //     if (texturesAppliedRef.current[child.name] === textureToApply) return;
  //     if (!materialCacheRef.current[child.name]) {
  //       const material = new THREE.MeshStandardMaterial({
  //         map: textureToApply,
  //         transparent: true,
  //         alphaTest: 0.05,
  //         side: THREE.DoubleSide,
  //       });
  //       materialCacheRef.current[child.name] = material;

  //       child.material.transparent = true;
  //       child.material.depthWrite = false;
  //       child.material = material;
  //       child.material.needsUpdate = true;

  //       texturesAppliedRef.current[child.name] = textureToApply;
  //     } else {
  //       child.material = materialCacheRef.current[child.name];
  //     }
  //   });
  // }, [ringObject, ringTextures, planet.name]);


  // Highlight the selected ring mesh by updating emissive color
  useEffect(() => {
    if (!ringObject) return;

    ringObject.traverse((child) => {
      if (child.isMesh && child.material) {
        const mat = child.material;
        if ('emissive' in mat) {
          const isSelected = child.name === selectedMeshInfo?.name;
          mat.emissive.set(isSelected ? 0xffa500 : 0x000000);
          mat.emissiveIntensity = isSelected ? 1 : 0;
          mat.transparent = isSelected ? false: true;
          mat.depthWrite = isSelected; // Prevents rings from being drawn behind when selected
          child.depthTest = true;
          child.depthWrite = true;
          child.renderOrder = 1;
        }
        mat.needsUpdate = true;
      }
    });
  }, [ringObject, selectedMeshInfo?.name]);

  // const createRedDot = (distanceFromCenter, heightOffset) => {
  //   const geometry = new THREE.SphereGeometry(0.05, 8, 8);
  //   const material = new THREE.MeshBasicMaterial({ color: 0xff0000 });
  //   const redDot = new THREE.Mesh(geometry, material);
  //   redDot.position.set(distanceFromCenter, heightOffset, 0);
  //   return redDot;
  // };

  // const sectionVelocities = calculateRingVelocities();

  // const RING_ROTATION_VISUAL_SCALE = 7500;

  // const ringRotationSpeeds = {
  //   inner: sectionVelocities.inner * RING_ROTATION_VISUAL_SCALE,
  //   main: sectionVelocities.main * RING_ROTATION_VISUAL_SCALE,
  //   outer: sectionVelocities.outer * RING_ROTATION_VISUAL_SCALE,
  // };

  const animatedMeshes = useRef([]); // Store animated meshes with metadata

  useEffect(() => {
    if (!ringObject) return;
    const meshes = [];
    ringObject.traverse((child) => {
      if (child.isMesh) {
        let ringType = null;
        // let dotRadius = 0;

        if (child.name.includes('Inner')) {
          ringType = 'inner';
          // dotRadius = radius * 1.2;
        } else if (child.name.includes('Main')) {
          ringType = 'main';
          // dotRadius = radius * 1.5;
        } else if (child.name.includes('Outer')) {
          ringType = 'outer';
          // dotRadius = radius * 1.8;
        }
        // if (ringType) {
        //   if (!child.redDot) {
        //     const redDot = createRedDot(dotRadius, 0);
        //     child.redDot = redDot;
        //     child.add(redDot);
        //   }

        //   meshes.push({
        //     mesh: child,
        //     redDot: child.redDot,
        //     speed: ringRotationSpeeds[ringType],
        //   });
        // }
      }
    });
    animatedMeshes.current = meshes;
  }, [ringObject, radius]);

  const spinCompleteFiredRef = useRef(false);

  useEffect(() => {
    if (!prevTrackingRef.current && isTrackingSatellite) {
      // Just started tracking, initiate one final spin
      // const currentY = meshRef.current?.rotation.y || 0;
      // targetRotationRef.current = currentY + Math.PI * 2; // One full rotation
      const targetDegrees = 280.46061837;
      const targetRadians = THREE.MathUtils.degToRad(targetDegrees);
      targetRotationRef.current = targetRadians;
      shouldCompleteSpinRef.current = true;
      currentRotationSpeedRef.current = rotationSpeed;
    }

    prevTrackingRef.current = isTrackingSatellite;
  }, [isTrackingSatellite, rotationSpeed]);

  
  useEffect(() => {
    // console.log("RESET SPIN TRIGGER FIRED");
    prevTrackingRef.current = false;
    spinCompleteFiredRef.current = false;
    shouldCompleteSpinRef.current = false;
    currentRotationSpeedRef.current = 0;

    scaleRef.current = 0;
    opacityRef.current = 0;

    if (meshRef.current) {
      const primeMeridianOffset = PLANET_DATA[planet.name]?.J2000PrimeMeridianOffset || 0;
      const rotationY = getEarthRotationAngle(primeMeridianOffset);
      meshRef.current.rotation.y = rotationY;
      meshRef.current.scale.setScalar(0);
    }

    if (planetModel?.scene) {
      planetModel.scene.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material.transparent = true;
          child.material.opacity = 0;
          child.material.depthTest = true;
          child.material.depthWrite = true;
          child.material.needsUpdate = true;
          child.material.renderOrder = 2;
        }
      });
    } else {
      console.warn("planetModel.scene is not ready");
    }

    opacityResetPendingRef.current = true;
  }, [resetSpinTrigger, planet.name]);

  // useFrame((_, delta) => {
  //   if (!meshRef.current) return;
  //   const mesh = meshRef.current;

  //   // Final spin + deceleration when tracking starts
  //   if (shouldCompleteSpinRef.current && isTrackingSatellite) {
  //     // REMOVE: scaleRef.current = 1;
  //     planetModel?.scene?.traverse((child) => {
  //       if (child.isMesh && child.material) {
  //         child.material.opacity = 1;
  //         child.material.transparent = false;
  //         child.material.needsUpdate = true;
  //       }
  //     });
  //     const targetRotation = targetRotationRef.current;
  //     let speed = currentRotationSpeedRef.current || 1.5;

  //     const currentY = mesh.rotation.y;
  //     const TWO_PI = Math.PI * 2;

  //     // Normalize angles to [0, TWO_PI)
  //     const currentNorm = (currentY % TWO_PI + TWO_PI) % TWO_PI;
  //     const targetNorm = (targetRotation % TWO_PI + TWO_PI) % TWO_PI;

  //     // Calculate shortest delta angle
  //     let deltaY = targetNorm - currentNorm;
  //     if (deltaY > Math.PI) deltaY -= TWO_PI;
  //     if (deltaY < -Math.PI) deltaY += TWO_PI;

  //     // Decelerate speed smoothly, clamp to minimum
  //     const deceleration = 0.015;
  //     speed = THREE.MathUtils.lerp(speed, 0, deceleration);
  //     speed = Math.max(speed, 0.01);
  //     currentRotationSpeedRef.current = speed;

  //     // Calculate rotation step and update rotation
  //     const rotationStep = Math.sign(deltaY) * Math.min(Math.abs(deltaY), speed * delta);
  //     mesh.rotation.y += rotationStep;

  //     // Snap to target if close enough
  //     if (Math.abs(deltaY) < 0.01) {
  //       mesh.rotation.y = targetRotation;
  //       shouldCompleteSpinRef.current = false;
  //       currentRotationSpeedRef.current = 0;

  //       if (!spinCompleteFiredRef.current) {
  //         onSpinComplete?.();
  //         spinCompleteFiredRef.current = true;
  //       }
  //     }
  //   }

  //   // When tracking satellite, skip normal rotation, but still run scale/opacity update
  //   if (!shouldCompleteSpinRef.current && !isTrackingSatellite) {
  //     mesh.rotation.y += rotationSpeed * delta;
  //   }

  //   // Run scale and opacity update regardless of tracking state
  //   if (scaleRef.current < 1) {
  //     scaleRef.current = Math.min(scaleRef.current + 0.3 * delta, 1);
  //     const newScale = scaleRef.current;
  //     mesh.scale.setScalar(newScale);
  //     ringObject?.scale.setScalar(newScale);
  //   }

  //   if (scaleRef.current >= 0.18 && opacityRef.current < 1) {
  //     opacityRef.current = Math.min(opacityRef.current + delta / 3, 1);

  //     planetModel?.scene?.traverse((child) => {
  //       if (child.isMesh && child.material) {
  //         child.material.opacity = opacityRef.current;
  //         child.material.transparent = opacityRef.current < 1;
  //         child.material.needsUpdate = true;
  //       }
  //     });
  //   }
  // });

  useFrame((_, delta) => {
    if (!meshRef.current) return;
    const mesh = meshRef.current;

    if (shouldCompleteSpinRef.current && isTrackingSatellite) {
      const targetRotation = targetRotationRef.current;
      let speed = currentRotationSpeedRef.current || 1.5;

      const currentY = mesh.rotation.y;
      const TWO_PI = Math.PI * 2;

      // Normalize angles to [0, TWO_PI)
      const currentNorm = (currentY % TWO_PI + TWO_PI) % TWO_PI;
      const targetNorm = (targetRotation % TWO_PI + TWO_PI) % TWO_PI;

      // Calculate shortest delta angle
      let deltaY = targetNorm - currentNorm;
      if (deltaY > Math.PI) deltaY -= TWO_PI;
      if (deltaY < -Math.PI) deltaY += TWO_PI;

      // Decelerate speed smoothly, clamp to minimum
      const deceleration = 0.015;
      speed = THREE.MathUtils.lerp(speed, 0, deceleration);
      speed = Math.max(speed, 0.01);
      currentRotationSpeedRef.current = speed;

      // Calculate rotation step and update rotation
      const rotationStep = Math.sign(deltaY) * Math.min(Math.abs(deltaY), speed * delta);
      mesh.rotation.y += rotationStep;

      // Snap to target if close enough
      if (Math.abs(deltaY) < 0.01) {
        mesh.rotation.y = targetRotation;
        shouldCompleteSpinRef.current = false;
        currentRotationSpeedRef.current = 0;

        if (!spinCompleteFiredRef.current) {
          onSpinComplete?.();
          spinCompleteFiredRef.current = true;     
        }
      }
    }
    if (!shouldCompleteSpinRef.current && !isTrackingSatellite) {
      // reset here
      mesh.rotation.y += rotationSpeed * delta;
    }

    if (scaleRef.current < 1) {
      scaleRef.current = Math.min(scaleRef.current + 0.3 * delta, 1);
      const newScale = scaleRef.current;
      mesh.scale.setScalar(newScale);
      ringObject?.scale.setScalar(newScale);
    }

    if (scaleRef.current >= 0.18 && opacityRef.current < 1) {
      opacityRef.current = Math.min(opacityRef.current + delta / 3, 1);

      planetModel?.scene?.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material.opacity = opacityRef.current;
          child.material.transparent = opacityRef.current < 1;
          child.material.needsUpdate = true;
        }
      });
    }

    if (opacityResetPendingRef.current && planetModel?.scene) {
      planetModel.scene.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material.opacity = 0;
          child.material.transparent = true;
          child.material.needsUpdate = true;
        }
      });
      opacityResetPendingRef.current = false;
    }
  });


  if (!texture) return null;

  return (
    <group ref={tiltGroupRef}>
      {/* Background Mesh*/}
      <mesh
        onPointerDown={(event) => {
          event.stopPropagation();
          setSelectedMeshInfo(null); // deselect on background click
        }}
        position={[0, 0, -5]} // slightly behind everything
      >
        <planeGeometry args={[100, 100]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>
      <group 
        ref={meshRef}
        // scale={[0, 0, 0]}
        scale={[1, 1, 1]}
        frustumCulled={false}
        onPointerDown={(event) => handleClick(event, 'Planet')}
        style={{ cursor: 'pointer' }}>
        <primitive object={planetModel.scene} />
      </group>

      {/* Ring Object as primitive */}
      {ringObject && (
        <primitive
          object={ringObject}
          onPointerDown={(event) => {
            // Find the clicked child mesh from the event intersections
            const intersected = event.intersections?.[0]?.object;
            if (intersected && intersected.name) {
              handleClick(event, intersected.name);
            }
            // else ignore click, no setState called
          }}
          style={{ cursor: 'pointer' }}
        />
      )}
    </group>
  );
}

function FixedCameraLook() {
  const { camera } = useThree();
  useFrame(() => {
    camera.lookAt(0, 0, 0);
  });
  return null;
}

function CleanupWrapper({ children, materialCacheRef }) {
  const { gl, scene } = useThree();

  useEffect(() => {
    return () => {
      scene.traverse((obj) => {
        if (obj.geometry && typeof obj.geometry.dispose === 'function') {
          obj.geometry.dispose();
        }

        if (obj.material) {
          const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
          materials.forEach((mat) => {
            if (mat && typeof mat.dispose === 'function') {
              mat.dispose();
            }
          });
        }
      });
      Object.values(materialCacheRef.current).forEach((mat) => {
        if (mat && typeof mat.dispose === 'function') mat.dispose();
      });
      materialCacheRef.current = {};
      // Optional: only dispose WebGL context if you're not using Canvas elsewhere
      gl.dispose(); 
    };
  }, [scene, materialCacheRef]);

  return <>{children}</>;
}

export default function Planet3DRenderer({
  planet,
  planetModel,
  texture,
  ringTextures,
  timelineImages,
  isExpanded,
  is3DViewEnabled,
  setIs3DViewEnabled,
  isTrackingSatellite = false,
  setIsTrackingSatellite,
  isExploringIss = false,
  setIsExploringIss,
  isExploringHst = false,
  setIsExploringHst,
  isExploringMars = false,
  setIsExploringMars,
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 800);
  const [selectedMeshInfo, setSelectedMeshInfo] = useState(null);
  const materialCacheRef = useRef({});
  const satelliteViewRef = useRef();
  const [filteredSatellites, setFilteredSatellites] = useState([]);
  const [conjunctions, setConjunctions] = useState([]);
  const [rtreeReady, setRtreeReady] = useState(false);
  const [spinCompleted, setSpinCompleted] = useState(false);
  const [clusterCount, setClusterCount] = useState(0);
  const [selectedSatelliteName, setSelectedSatelliteName] = useState('');
  const [selectedSatelliteCountry, setSelectedSatelliteCountry] = useState('');
  const [selectedSatelliteLaunchDate, setSelectedSatelliteLaunchDate] = useState('');
  const [selectedSatelliteLat, setSelectedSatelliteLat] = useState('');
  const [selectedSatelliteLon, setSelectedSatelliteLon] = useState('');
  const [selectedSatelliteAltitude, setSelectedSatelliteAltitude] = useState('');
  const [selectedSatelliteVelocity, setSelectedSatelliteVelocity] = useState('');
  const [isIssSelected, setIsIssSelected] = useState(false);
  const [isHstSelected, setIsHstSelected] = useState(false);
  const [disposeAssets, setDisposeAssets] = useState(() => () => {});
  const [resetSpinTrigger, setResetSpinTrigger] = useState(0);
  const [isGlowing, setIsGlowing] = useState(false);
  const [isExploreClicked, setIsExploreClicked] = useState(false);
  const [showGallery, setShowGallery] = useState(false); 
  const [clonedHubbleModel, setClonedHubbleModel] = useState(null);
  const marsExplorationRef = useRef();
  const [isConjunctionModeActive, setIsConjunctionModeActive] = useState(false);
  const [paleoTexture, setPaleoTexture] = useState(null);


  useEffect(() => {
    setPaleoTexture(null);
  }, [planet?.name]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 800);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (isExploringMars) {
        marsExplorationRef.current?.shutdown();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePointerDown = async () => {
    setIsGlowing(true);
    await onExploreIssHstClick(); // Wait before proceeding
  };

  const handlePointerUp = () => {
    setIsGlowing(false);
  };

  const handlePointerLeave = () => {
    setIsGlowing(false);
  };

  const ROTATION_VISUAL_SCALE = 75;
  const rotationSpeed = useMemo(() => {
    if (!planet) return 0;
    const baseSpeed = getRotationSpeed(planet.name) || 1;
    return baseSpeed * ROTATION_VISUAL_SCALE;
  }, [planet]);

  if (!planet) return null;

  const onReleaseAssets = async ({ fromExploreButton = false, fromIssBackButton = false } = {}) => {
    if (isTrackingSatellite) {
      satelliteViewRef.current?.resetCamera();
      await disposeAssets();

      setSelectedSatelliteName('');
      setSelectedSatelliteCountry('');
      setSelectedSatelliteLaunchDate('');
      setRtreeReady(false);
      setClusterCount(0);
      setFilteredSatellites([]);
      setIsTrackingSatellite(false);
      setIsIssSelected(false);
      setIsHstSelected(false);
      // setSpinCompleted(false);
      if (!fromExploreButton) {
        setSelectedSatelliteLat('');
        setSelectedSatelliteLon('');
        setSelectedSatelliteAltitude('');
        setSelectedSatelliteVelocity('');
        setIsExploringIss(false);
        setIsExploringHst(false);
        setIsExploreClicked(false);
        setIsTrackingSatellite(false);
        setIs3DViewEnabled(true);// remain in non-3D view
        setSpinCompleted(true);// trigger SatelliteView
      }

    } else if (fromIssBackButton) {
      // Coming back from ISS view to SatelliteView
      await disposeAssets({ fromBackButton: true });
      setIsExploringIss(false);
      setIsExploringHst(false);
      setIsExploreClicked(false);
      setIsTrackingSatellite(true);
      setIs3DViewEnabled(false);// remain in non-3D view
      setSpinCompleted(true);// trigger SatelliteView
      // setRtreeReady(true);
      setRtreeReady(false);

       setTimeout(() => setRtreeReady(true), 50); 
    }
    setResetSpinTrigger(prev => prev + 1);
  };

  const onExploreIssHstClick = async () => {
    setIs3DViewEnabled(false);

    if (isIssSelected) {
      setIsExploringIss(true);
      setIsExploreClicked(true);
    } else if (isHstSelected) {
      console.log("EXPLORE HST");
      setIsExploringHst(true);
      setIsExploreClicked(true);
    }

    await onReleaseAssets({ fromExploreButton: true }); // Don't re-enable 3D view
    await new Promise(resolve => requestAnimationFrame(resolve));

    if (isMobile) {
      await new Promise(res => setTimeout(res, 100));
    }
  };

  
  return (
    <>
      {(!isExploringIss && !isExploringHst && !isExploringMars) && (
        <div
          style={{
            height: '100vh',
            width: '100vw',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 5,
            // pointerEvents: 'none',
            transform: isMobile && isExpanded ? 'translateY(-12vh)' : 'translateY(0)',
            transition: 'transform 0.4s ease',
          }}
        >
          <Canvas
            camera={{ position: [3, 2, 10], fov: 45 }}
            gl={{ outputEncoding: THREE.SRGBColorSpace }}
            onCreated={({ gl }) => {
              gl.toneMapping = THREE.ACESFilmicToneMapping;
              gl.toneMappingExposure = 1;
            }}
          >
            <CleanupWrapper materialCacheRef={materialCacheRef}>
              {!isTrackingSatellite ? (
                <FixedCameraLook />
              ) : (
                spinCompleted &&
                rtreeReady && (
                  <SatelliteView
                    // key={JSON.stringify(filteredSatellites.map(s => s.satelliteId))}
                    ref={satelliteViewRef}
                    satellites={filteredSatellites}
                    onClusterCount={setClusterCount}
                    setSatelliteName={setSelectedSatelliteName}
                    setSatelliteCountry={setSelectedSatelliteCountry}
                    setLaunchDate={setSelectedSatelliteLaunchDate}
                    setSatelliteLat={setSelectedSatelliteLat}
                    setSatelliteLon={setSelectedSatelliteLon}
                    setSatelliteAltitude={setSelectedSatelliteAltitude}
                    setSatelliteVelocity={setSelectedSatelliteVelocity}
                    setDisposeAssets={setDisposeAssets}
                    setIsIssSelected={setIsIssSelected}
                    setIsHstSelected={setIsHstSelected}
                    setClonedModel={setClonedHubbleModel}
                    setConjunctions={setConjunctions}
                    isConjunctionModeActive={isConjunctionModeActive}
                  />
                )
              )}

              <ambientLight intensity={0.5} />
              <directionalLight position={[5, 5, 10]} intensity={0.25} />
              <directionalLight position={[-5, -5, -10]} intensity={0.35} />
              <SpinningPlanet
                planet={planet}
                planetModel={planetModel}
                texture={texture}
                paleoTexture={planet.name === 'Earth' ? paleoTexture : null}
                ringTextures={ringTextures}
                rotationSpeed={rotationSpeed}
                materialCacheRef={materialCacheRef}
                selectedMeshInfo={selectedMeshInfo}
                setSelectedMeshInfo={setSelectedMeshInfo}
                isTrackingSatellite={isTrackingSatellite}
                onSpinComplete={() => setSpinCompleted(true)}
                resetSpinTrigger={resetSpinTrigger}
              />
            </CleanupWrapper>
          </Canvas>
        </div>
      )}

      {(!isExploringIss && !isExploringHst && !isExploringMars && !isTrackingSatellite && planet.name === 'Earth') && (
        <PaleoEarthTimeline 
          timelineImages={timelineImages}
          onTextureReady={(texture) => setPaleoTexture(texture)}
        />
      )}


      {(!isExploringIss && !isExploringHst && !isExploringMars) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10000,
            // pointerEvents: 'none',
            padding: '1rem',
          }}
        >
          {/* {isTrackingSatellite && spinCompleted && ( */}
          {isTrackingSatellite && spinCompleted && (         
              <SatelliteFilterControls
                onFilter={setFilteredSatellites}
                onLoadComplete={setRtreeReady}
                clusterCount={clusterCount}
                satelliteName={selectedSatelliteName}
                satelliteCountry={selectedSatelliteCountry}
                launchDate={selectedSatelliteLaunchDate}            
                isTrackingSatellite={isTrackingSatellite}
                setIsTrackingSatellite={setIsTrackingSatellite}
                conjunctions={conjunctions}
                onConjunctionModeChange={setIsConjunctionModeActive}
              />
          )}

          {(isIssSelected || isHstSelected) && !isExploreClicked && (
            <div className="center-button-wrapper">
              <button
                className={`explore-iss-button ${isGlowing ? "glow" : ""}`}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerLeave}
              >
                EXPLORE
                <br />
                {isIssSelected && <small>ISS Live Data & Images</small>}
                {isHstSelected && <small>Hubble Live Data & Images</small>}
                <span className="explore-subtext">[CLICK HERE]</span>
              </button>
            </div>
          )}
        </div>
      )}

      {(isExploringIss || isExploringHst || isExploringMars) && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: '#000',
            zIndex: 9999,
          }}
        >

          <Suspense fallback={<div style={{ color: 'white' }}></div>}>
            {isExploringIss &&  (
              <LazyExploreISS 
                satelliteLat={selectedSatelliteLat}
                satelliteLon={selectedSatelliteLon}
                satelliteAltitude={selectedSatelliteAltitude}  
                satelliteVelocity={selectedSatelliteVelocity}  
              />
            )}
            {isExploringHst &&  (
              <LazyExploreHST 
                clonedModel={clonedHubbleModel}
                satelliteLat={selectedSatelliteLat}
                satelliteLon={selectedSatelliteLon}
                satelliteAltitude={selectedSatelliteAltitude}  
                satelliteVelocity={selectedSatelliteVelocity}    
              />
            )}
            {isExploringMars && <LazyMarsExploration ref={marsExplorationRef} />}
            {(showGallery && isExploringIss) && (
              <NASAImageGallery 
                category="iss" 
                onClose={() => setShowGallery(false) }
              />
            )}
            {(showGallery && isExploringHst) && (
              <NASAImageGallery 
                category="hst" 
                onClose={() => setShowGallery(false)}
              />
            )}
            {(!showGallery && !isExploringMars) && (
              <div
                style={{
                  position: 'fixed',
                  bottom: 'calc(2rem + env(safe-area-inset-bottom))',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 10001,
                }}
              >
                <button
                  onClick={() => setShowGallery(true)}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    border: '1px solid white',
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  Open Gallery
                </button>
              </div>
            )}
          </Suspense>

        </div>
      )}

      {(isExploringIss || isExploringHst || isExploringMars || isTrackingSatellite) && (
        <>
        <div className="back-button">
          <button onClick={async () => {
            if (isExploringMars) {
              await marsExplorationRef.current?.shutdown();
              setIsExploringMars(false);
            } else {
              onReleaseAssets({ fromIssBackButton: true });
            }
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path
                fillRule="evenodd"
                d="M15 8a.5.5 0 0 1-.5.5H3.707l4.147 4.146a.5.5 0 0 1-.708.708l-5-5a.5.5 0 0 1 0-.708l5-5a.5.5 0 1 1 .708.708L3.707 7.5H14.5a.5.5 0 0 1 .5.5z"
              />
            </svg>
          </button>
        </div>
        </>       
      )}
      {selectedMeshInfo && (
        <SpeechBubble
          label={PlanetLabel(planet.name, selectedMeshInfo.name)}
          isOpen={!!selectedMeshInfo}
        />
      )}
    </>
  );
}
